import { BillingInterval } from '@this+that/models';

import { InvoiceStatus } from '../../components/billing/constants/invoiceStatus';

const MINOR_UNIT_DIVISOR = 100;
const EXPIRY_MONTH_PAD_LENGTH = 2;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS_PER_YEAR = 12;
const PERCENT_MULTIPLIER = 100;

// Stripe invoice statuses (draft/open/paid/uncollectible/void) collapsed onto
// the shared InvoicesCard's simpler paid/pending/failed vocabulary.
const FAILED_STRIPE_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  'uncollectible',
  'void',
]);

export function mapStripeInvoiceStatus(status: string): InvoiceStatus {
  if (status === 'paid') {
    return InvoiceStatus.PAID;
  }
  if (FAILED_STRIPE_INVOICE_STATUSES.has(status)) {
    return InvoiceStatus.FAILED;
  }
  return InvoiceStatus.PENDING;
}

export function formatBillingAmount(
  amountMinorUnits: number,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinorUnits / MINOR_UNIT_DIVISOR);
}

export function formatCardExpiry(expMonth: number, expYear: number): string {
  const paddedMonth = String(expMonth).padStart(EXPIRY_MONTH_PAD_LENGTH, '0');
  return `${paddedMonth} / ${expYear}`;
}

const TRIAL_END_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

export function formatTrialEndDate(
  timestampMs: number,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, TRIAL_END_DATE_FORMAT_OPTIONS).format(
    new Date(timestampMs),
  );
}

export function getDaysUntil(
  timestampMs: number,
  now: number = Date.now(),
): number {
  return Math.max(0, Math.ceil((timestampMs - now) / ONE_DAY_MS));
}

// Unit-agnostic: works identically whether unitAmount is cents or whole
// currency units, since only the ratio between the two prices matters.
export function calculateAnnualDiscountPercent(
  monthlyUnitAmount: number | undefined,
  annualUnitAmount: number | undefined,
): number | undefined {
  if (!monthlyUnitAmount || annualUnitAmount === undefined) {
    return undefined;
  }
  const annualMonthlyEquivalent = annualUnitAmount / MONTHS_PER_YEAR;
  return Math.round(
    (1 - annualMonthlyEquivalent / monthlyUnitAmount) * PERCENT_MULTIPLIER,
  );
}

export interface IntervalPriceDisplay {
  amountMinorUnits: number;
  suffixKey: string;
}

// YEAR shows the monthly-equivalent amount (annual total / 12) with a suffix
// clarifying it's billed annually, instead of the raw annual total mislabeled
// with the monthly suffix.
export function getIntervalPriceDisplay(
  interval: BillingInterval,
  unitAmount: number,
): IntervalPriceDisplay {
  if (interval === BillingInterval.YEAR) {
    return {
      amountMinorUnits: unitAmount / MONTHS_PER_YEAR,
      suffixKey: 'billing.plans.priceUnitAnnual',
    };
  }
  return {
    amountMinorUnits: unitAmount,
    suffixKey: 'billing.plans.priceUnit',
  };
}
